import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ChevronRight, Package, Leaf, Eye, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import OwnershipFooter from '../components/common/OwnershipFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = ({ onLogin, onLogout }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const roles = [
    {
      id: 'supply_chain_manager',
      title: 'Supply Chain Manager',
      description: 'Full operational oversight of batches, checkpoints, and logistics flows',
      icon: Package,
    },
    {
      id: 'sustainability_manager',
      title: 'Sustainability Manager',
      description: 'Environmental metrics, compliance monitoring, and ESG reporting controls',
      icon: Leaf,
    },
    {
      id: 'external_viewer',
      title: 'External Auditor',
      description: 'Read-only traceability and audit visibility',
      icon: Eye,
    },
  ];

  const handleRoleSelect = async (roleId) => {
    setLoading(true);
    try {
      const gateToken = localStorage.getItem('mvp_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { role: roleId },
        gateToken ? { headers: { Authorization: `Bearer ${gateToken}` } } : {},
      );

      // Backend re-issued a role-bearing JWT. Replace the gate token so all
      // subsequent API calls (Authorization: Bearer mvp_token) carry the role
      // claim and are correctly authorised on the server.
      if (response.data?.token) {
        localStorage.setItem('mvp_token', response.data.token);
      }

      toast.success('Login successful!');
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error?.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-8 py-12 bg-white">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_farm-to-table-135/artifacts/jfihotrx_IMG_5685.png" 
              alt="PureSustain Logo" 
              className="w-full h-auto"
              style={{ 
                maxHeight: '200px', 
                objectFit: 'contain',
                filter: 'contrast(1.35) brightness(1.15) saturate(1.2)',
                imageRendering: '-webkit-optimize-contrast',
                WebkitFontSmoothing: 'antialiased',
                opacity: 1
              }}
            />
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-800 mb-2 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Access Portal
            </h2>
            <p className="text-slate-500 font-normal">
              Aquaculture transparency platform — choose your authorized role
            </p>
          </div>

          <div className="space-y-4" data-testid="role-selector">
            {roles.map((role) => {
              const IconComponent = role.icon;
              return (
                <button
                  key={role.id}
                  data-testid={`role-button-${role.id}`}
                  onClick={() => handleRoleSelect(role.id)}
                  disabled={loading}
                  className="w-full text-left p-6 rounded-xl border-2 border-slate-400 hover:border-slate-600 hover:bg-slate-50 transition-all duration-200 bg-white group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <IconComponent className="w-5 h-5 text-slate-600" />
                        <h3 className="text-lg font-bold text-slate-800 group-hover:text-slate-800 transition-colors">
                          {role.title}
                        </h3>
                      </div>
                      <p className="text-sm text-slate-500 leading-relaxed">{role.description}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 group-hover:translate-x-1 transition-all flex-shrink-0 ml-3" />
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-10 pt-6 border-t border-slate-300">
            <OwnershipFooter variant="inline" />
            {onLogout && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onLogout}
                  data-testid="role-portal-signout"
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  <LogOut className="w-3 h-3 mr-1.5" />
                  Sign out of secure access
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div
        className="hidden lg:flex lg:flex-1 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1765315163435-487b36141c22?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-slate-900/30"></div>
        <div className="relative z-10 flex items-end pb-32 p-12">
          <div className="text-white">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              End-to-End Aquaculture Transparency
            </h2>
            <p className="text-base text-slate-100 max-w-lg">
              PureSustain provides visibility across every stage of aquaculture
              production — from hatchery to consumer. The platform combines
              traceability, IoT sensors, and real-time analytics to strengthen
              compliance, improve sustainability, and restore trust.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
